import { Dispatch, SetStateAction } from "react";
import { TooltipProps } from "recharts";
import dayjs from "dayjs";
import { QuantComponentsRenderedFromType } from "src/pages/NewAskKural/models/interfaces";
import { ChartConfig } from "src/components/Charts/models/interfaces";

export enum userSelectedType {
	question = "question",
	subSection = "sub-section",
}

export enum chartErrorType {
	INVALID_FIELD = "INVALID_FIELD",
	NO_RECORDS = "NO_RECORDS",
	NO_DATA = "NO_DATA",
	CALCULATION_ERROR = "CALCULATION_ERROR",
	INVALID_CONFIGURATION = "INVALID_CONFIGURATION",
}

export const colors = [
	"bg-red-200",
	"bg-blue-200",
	"bg-green-200",
	"bg-pink-200",
	"bg-yellow-200",
	"bg-purple-200",
	"bg-indigo-200",
	"bg-teal-200",
	"bg-gray-200",
	"bg-orange-200",
	"bg-lime-200",
	"bg-emerald-200",
	"bg-cyan-200",
	"bg-amber-200",
	"bg-fuchsia-200",
];

export const countColors = [
	"bg-blue-300",
	"bg-purple-400",
	"bg-fuchsia-300",
	"bg-pink-300",
	"bg-indigo-300",
	"bg-violet-300",
	"bg-purple-200",
	"bg-rose-200",
	"bg-sky-200",
	"bg-gray-200",
];

export const countsColorMap = {
	"bg-blue-300": "var(--neutral-white)",
	"bg-purple-400": "var(--ai-accent-color)",
	"bg-fuchsia-300": "var(--neutral-white)",
	"bg-pink-300": "var(--error-red)",
	"bg-indigo-300": "var(--secondary-color-2)",
	"bg-violet-300": "var(--neutral-white)",
	"bg-purple-200": "var(--neutral-white)",
	"bg-rose-200": "var(--error-red)",
	"bg-sky-200": "var(--neutral-white)",
	"bg-gray-200": "var(--neutral-white)",
};

export const colorMap = {
	"bg-red-200": "var(--error-red)",
	"bg-blue-200": "var(--neutral-white)",
	"bg-green-200": "var(--success-green)",
	"bg-pink-200": "var(--neutral-white)",
	"bg-yellow-200": "var(--warning-orange)",
	"bg-purple-200": "var(--neutral-white)",
	"bg-indigo-200": "var(--neutral-white)",
	"bg-teal-200": "var(--success-green)",
	"bg-gray-200": "var(--neutral-white)",
	"bg-orange-200": "var(--warning-orange)",
	"bg-lime-200": "var(--success-green)",
	"bg-emerald-200": "var(--success-green)",
	"bg-cyan-200": "var(--neutral-white)",
	"bg-amber-200": "var(--warning-orange)",
	"bg-fuchsia-200": "var(--neutral-white)",
};

const hexToRgba = (hex: string, opacity: number) => {
	const bigint = parseInt(hex.replace("#", ""), 16);
	const r = (bigint >> 16) & 255;
	const g = (bigint >> 8) & 255;
	const b = bigint & 255;

	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const baseColors = ["var(--warning-orange)", "var(--secondary-color-2)", "var(--error-red)", "var(--success-green)", "var(--warning-orange)", "var(--primary-color)", "var(--warning-orange)", "var(--secondary-color-2)", "var(--error-red)", "var(--success-green)", "var(--warning-orange)", "var(--primary-color)"];

export const segmentcolors = baseColors.map((color) => hexToRgba(color, 0.1));
export const segmentBGcolors = baseColors.map((color) => hexToRgba(color, 0.05));
export const segmentTextcolors = baseColors.map((color) => `${color}`);
export const segmentBordercolors = baseColors.map((color) => hexToRgba(color, 0.1));
export const segmentActiveBorderColors = baseColors.map((color) => hexToRgba(color, 0.3));

export enum chartType {
	Bar = "Bar",
	Simple_Bar = "Simple_Bar",
	Stacked_Column = "Stacked_Column",
	Stacked_Bar = "Stacked_Bar",
	Clustered_Column = "Cluster_Column",
	Clustered_Bar = "Cluster_Bar",
	Line_Horizontal = "Line_Horizontal",
	Pie = "Pie",
	Metrics = "Metrics",
	Human_Chart = "Human_Chart",
	Line_Vertical = "Line_Vertical",
	Histogram_Chart = "Histogram_Chart",
	Open_Ended = "oe",
	MultiColor_Histogram_Chart = "MultiColor_Histogram_Chart",
}

export enum AnalysisType {
	Top_Box = "Top Box",
	Mean = "Mean",
	NSize = "n_size",
	NSizePercentage = "Proportion",
	Ratio = "Ratio",
}

export enum DropDownTypes {
	Split_by = "Compare",
	Filters = "Filters",
	x_axis_field = "X-axis",
}

export enum NumberFormat {
	"##" = "TWO_DIGITS",
	"##.#" = "TWO_DIGITS_DECIMAL_SINGLE",
	"##.##" = "TWO_DIGITS_DECIMAL_DOUBLE",
	"##%" = "TWO_DIGITS_PERCENT",
	"##.#%" = "TWO_DIGITS_DECIMAL_SINGLE_PERCENT",
	"##.##%" = "TWO_DIGITS_DECIMAL_DOUBLE_PERCENT",
}

export enum SummaryStatus {
	Success = "success",
	Not_Found = "not_found",
	Failed = "failed",
	In_Progress = "in_progress",
}

export const barCharts = [
	"Bar",
	"Simple_Bar",
	"Stacked_Column",
	"Stacked_Bar",
	"Cluster_Column",
	"Cluster_Bar",
	"stackedColumn",
	"stackedBar",
	"clusteredColumn",
	"clusteredBar",
	"simpleBar",
];

export interface IFilterData {
	title: string;
	value: string;
	key: string;
	children?: {
		title: string;
		value: string;
		key: string;
	};
}

export interface ApiResponse<T> {
	data: T;
	error: string;
	message: string;
	is_error: boolean;
}

export interface IAnalysisData {
	// Define the shape of Analysis data if known
}

export interface ISectionData {
	xAxis: string;
	series: { name: string; value: number; n_size?: string }[];
	n_size?: { min?: number; max?: number } | number | null;
}

export interface ISection {
	chart_title?: string;
	chart_type?: string;
	x_axis_values?: string[];
	y_axis_values?: string[];
	x_axis_label?: string;
	y_axis_label?: string;
	data?: ISectionData[];
	oe_data: any;
	position?: number;
	format?: string;
	footnote?: string;
	chart_config?: { ser_color?: Record<string, string> };
	question?: string;
	question_title?: string;
	question_count?: number;
	name?: string;
	analysis_type?: string;
	is_grid?: boolean;
	is_weight?: boolean;
	value_params?: any;
	error?: any;
	axis_idf?: any;
}

export interface IMasterDropdownData {
	spilt_by: {
		title: string;
		value: string;
		key: string;
		children: {
			title: string;
			value: string;
			key: string;
		}[];
	}[];
	add_filter: {
		title: string;
		key: string;
		children: {
			title: string;
			key: string;
		}[];
	}[];
	chart_panel: {
		title: string;
		value: string;
		key: string;
	}[];
}

export interface DataType {
	key: React.Key;
	kpi: string;
	wave01: string;
	wave02: string;
	wave03: string;
	wave04: string;
	wave05: string;
}

export interface FilterTreeSelectProps {
	checkedKeys: any[];
	setCheckedKeys: Dispatch<SetStateAction<any[]>>;
}

export interface IFilterItem {
	title: string;
	value: string;
	children?: IFilterItem[];
}

export interface ICheckboxMenuProps {
	checkedKeys: any;
	setCheckedKeys: (keys: any[]) => void;
}

export interface IChartPanelPropsNew {
	selected: number;
	setSelected: (index: number) => void;
	items: { [key: number]: any };
	loadingMore: boolean;
	chartLoading: { [key: number]: boolean };
	loadNextBatch: (forceReset?: boolean) => void;
	setLoadingMore: (value: boolean) => void;
	failedCharts?: {
		[key: number]:
			| boolean
			| {
					message?: string;
					isRetry?: boolean;
			  };
	};
	visibleCount: number;
	isLoadingMoreDisplay: boolean;
	virtualState: any;
	availableChartIndices?: number[];
	visibleIndices?: number[];
	onScroll?: () => void;
	leftPanelAvailableChartIndices?: number[];
	loadPageForPlaceholder?: (index: number) => void;
	isFilterApplied: boolean;
	isDrawerFilterApplied: boolean;
	q_count: number;
	scrollDirection: string;
	isScrolling: boolean;
	sectionName: string;
	executiveSummaryObj: {
		tab_name: string;
		sections: [];
	};
}

export interface Position {
	row: number;
	column: number;
}

export interface DataItem {
	chartTitle: string;
	questions_count?: any;
	position: Position;
	chartType: string;
	colorLegends: any;
	graph_customization: any;
	footnote: string;
	name: String;
}

export interface IFilter {
	label: string;
	key: string;
	selected_bucket: {
		label: string;
		key: string;
		count: number;
		values: [];
	}[];
	is_custom: boolean;
	is_float: boolean;
	is_question: boolean;
}

export interface ISubSegment {
	_id: number;
	name: string;
	filters: IFilter[];
	n_size: number;
	dimensions: number;
	isEditable: boolean;
	bgColor?: string;
	count?: number;
	loadingSpin?: boolean;
}

export interface ISegment {
	_id?: string;
	name: string;
	description: string;
	category: string[];
	sub_seg: ISubSegment[];
}

export interface IStudyState {
	study_id: string;
	segment: ISegment;
}

export interface IValue {
	label: string;
	key: string;
	count: string;
	level?: number; // Optional, as not all levels have this field
	values: IValue[]; // Recursive type for nested values
	questionSummary?: string;
	is_custom: boolean;
	is_float: boolean;
	is_question: boolean;
}

// Interface for the main data array
export interface ICategory {
	label: string;
	key: string;
	count: string;
	level?: number;
	values: IValue[];
	questionSummary?: string; // Optional for categories without this field
	is_custom: boolean;
	is_float: boolean;
	is_question: boolean; // Optional for categories without this field
}

export interface IErrorResponse {
	type: string;
	name: string;
	message: string;
}

export interface ISegmentData {
	basicSegmentDetails: { id: string; name: string; description: string };
	createSegmentDetails: IStudyState;
	activeSubSegment: ISubSegment | null;
	categoryDetails: ICategory[];
	originalCategoryDetails: ICategory[];
	isCreateSegment: boolean;
	listOfSegments: ISegment[];
	loadingSpin: boolean;
	btnLoading: boolean;
	btnDisabled: boolean;
	isEditable: boolean;
	isSegmentLoading: boolean;
	hasError: boolean;
	errorResponse: IErrorResponse[];
	isReapplySegment: boolean;
	isReapplyBtnEnabled: boolean;
	isReapplyModalOpen: boolean;
}

export interface IDynamicChartsProps {
	wholeData?: any[]; // You can replace `any` with a more specific type for your chart data
	obj: any; // You can replace `any` with a more specific type for your chart data
	type: string;
	config: any;
	isMini: boolean;
	legends?: any;
	showYAxisLabel?: boolean;
	showXAxis?: boolean;
	maxDomain?: number | string;
	minDomain?: number | string;
	loading?: boolean;
	chartSegmentLength?: number;
	isSectionalSummary?: boolean;
	renderedFrom?: QuantComponentsRenderedFromType;
	viewType?: ChartViewType;
	grid?: ChartGridType;
}

// Define the input data structure
interface InputDataItem {
	name: string;
	[key: string]: string | number;
}

// Define the structure for our transformed data
export interface ITableDataItem {
	key: string;
	kpi?: string;
	selected?: boolean;
	[key: string]: string | number | boolean;
}

export interface CombinedTableDataItem extends ITableDataItem {
	[key: string]: string | number | boolean;
}

export interface SectionalSummaryTableProps {
	data: InputDataItem[];
	allData?: any;
	title_chart?: string;
	q_num?: string;
	dlabel_fmt: string;
	activeSegments: string[];
	chartObj: any;
	renderedFrom?: QuantComponentsRenderedFromType;
}

interface ICategoryItem {
	label: string;
	key: string;
	id: string | null;
	count: number;
}

export type CategoryMap = Record<string, Record<string, ICategoryItem>>;

export interface Entry {
	name: string;
	[key: string]: string | number;
}

export interface ChartLegendProps {
	data: any;
	className: string;
	style: React.CSSProperties;
}

// Update the interface to include the new props
export interface IChartLegendsProps {
	data: any;
	className: string;
	style: React.CSSProperties;
	legends: string[];
	handleLegends: (key: string) => void;
	activeSegments: string[];
	renderedFrom?: QuantComponentsRenderedFromType;
	isWidgetExpanded?: boolean;
}

export interface ChartLegendPropsMainChart {
	data: any;
	className: string;
	style: React.CSSProperties;
}

export interface StyleConstantsChart {
	yAxisLabelSize: number;
	yAxisLabelFontWeight: number;
	yAxisLabelColor: string;
	maxBoxWidth: number;
	padding: number;
}
export interface ChartStyleConfigLine {
	marginLeftMini: number;
	yAxisLabelOffset: number;
	activeDotSize: number;
	yAxisLabelSize: number;
	yAxisLabelFontWeight: number;
	yAxisLabelColor: string;
}

export interface CustomTooltipProps<ValueType extends string | number | (string | number)[] = number, NameType extends string | number = string> extends TooltipProps<
	ValueType,
	NameType
> {
	obj: any;
	customConfig?: ChartConfig["config"] | undefined;
}

export interface CustomTooltipLineProps extends TooltipProps<number, string> {
	chartDataTooltip?: any[];
	graphCustomization?: {
		dlabel_fmt?: string;
		line_chart_markers?: any;
		line_marker_size?: any;
	};
	setIsTooltipHovered?: any;
	type?: string;
	obj?: any;
	config?: any;
	chartTypeTooltip?: string;
	hoveredBarKey?: string;
	customConfig?: any;
}

export const dropdownConfigs = [
	{ placeholderText: DropDownTypes.Filters, minWidth: "14rem" },
	// { placeholderText: DropDownTypes.x_axis_field, width: "10rem" },
	{ placeholderText: DropDownTypes.Split_by, width: "14rem" },
];

export interface ChartItem {
	question_number?: string;
	[key: string]: any; // prevents accessing unknown props without narrowing
}

export interface IAllChartsCacheItem {
	studyId: string;
	studyName: string;
	lastUpdatedAt: string;
	tabs: {
		tabName: string;
		sections: {
			sectionName: string;
			charts: {
				pageNo: number;
				dropdownConfig: { [key: string]: string | string[] };
				aiSummary?: any;
				data: any[];
			}[];
		}[];
	}[];
}

export interface IFlatChartsCacheItem {
	hashKey: string;
	studyId: string;
	studyName: string;
	activeInsightsId: string;
	lastUpdatedAt: string;
	tabName: string;
	sectionName: string;
	pageNo: number;
	dropdownConfig: { [key: string]: string | string[] };
	filters: any[];
	splitBy: any;
	data?: any[];
	aiSummary?: any[];
	metadata?: {
		totalPages?: number;
		totalItems?: number;
		pageSize?: number;
		currentPage?: number;
		createdAt?: string | dayjs.Dayjs;
	};
}

export interface IFlatChartsCache {
	[hashKey: string]: IFlatChartsCacheItem;
}

export interface TableDataItem {
	key: string;
	sectionName: string;
	chartName: string;
	chartData: any;
	chartId: string;
	selected: boolean;
	indeterminate?: boolean;
	hashKey?: string | null;
	tabName: string;
	pageNo: number;
	dropdownConfig?: any;
	children?: TableDataItem[];
	isParent?: boolean;
	isChild?: boolean;
	isSection?: boolean;
	isSelectAll?: boolean;
	parentKey?: string;
	[key: string]: any;
}

export interface IDisplayState {
	visibleCount: number;
	isLoadingMoreDisplay: boolean;
	totalPages: number | null;
}

export interface IAllChartSummary {
	pageNo: number | null;
	summaryText: string;
	loading: boolean;
	error: boolean;
	isStale?: boolean;
	status?: string;
}

export interface IChartViewStatus {
	pageNo: number;
	isChartView: boolean;
}

export interface IDynamicDropdownsProps {
	renderedFrom?: string;
	mainChartData?: { dropdowns?: any[] };
	setChartState?: (state: any) => void;
	chartState?: any;
	selectedAnalysisChart?: any;
	executiveSummaryObj?: any;
	sectionName?: string;
	chartIndex?: number;
	setUpdatedTransformedChartData?: (data: any) => void;
	setChartInd?: (index: number) => void;
	setChartLoading?: (loading: boolean) => void;
	setDropdownsUpdated?: (fn: (prev: number) => number) => void;
	setCustomDropdownConfig?: (config: any) => void;
	masterDropDown?: any;
	selectedStudyId: string;
	selectedSegments___;
	filterArray;
	payloadConfig?: any;
	setChartSegments?;
	activeSegments?: [];
	dropdownDisabled?: boolean;
	setDropDownValues?: (dropDowns: any) => void;
}

export interface ITemplate {
	template_id: string;
	template_name: string;
	template_description?: string;
	is_default?: boolean;
	is_base_template?: boolean;
}

export interface IChartModalContainerProps {
	activeTab: string | number;
}

export interface ISegmentFilterMemoizedProps {
	// formattedArray: string[];
	// handleRemoveFilter: (filterName: string) => void;
	// handleClearAll: () => void;
	renderedFrom: QuantComponentsRenderedFromType;
	filterArray: string[];
	setFilterArray: React.Dispatch<React.SetStateAction<string[]>>;
}

export enum ChartViewType {
	Dashboard = "dashboard",
	Widget = "widget",
	Mini = "mini",
}

export enum ChartGridType {
	oneXone = "1x1",
	oneXtwo = "1x2",
	oneXthree = "1x3",
}

// Constants for SectionalMenuDashboard
export const DEFAULT_PAGE_SIZE = 1;
export const BATCH_PAGE_SIZE = 5;

// Enhanced Virtual Scrolling Constants
export const VIRTUAL_SCROLL_THRESHOLD = 10;
export const VISIBLE_CHART_COUNT = 10;
export const CHART_HEIGHT_ESTIMATE = 680;
export const SCROLL_DEBOUNCE_MS = 10;
export const BATCH_SIZE = 5; // Number of charts to add/remove at once
export const SCROLL_DOWN_EDGE_THRESHOLD = 280;
export const SCROLL_UP_EDGE_THRESHOLD = 260;
export const CHART_PANEL_SCROLL_THRESHOLD = 50;

// Default states
export const DEFAULT_CHART_STATE = {
	sectionId: "",
	requestId: Date.now(),
	loadedCharts: {},
	loadingCharts: {},
	failedCharts: {},
	loadedPages: new Set(),
	failedPages: new Set(),
	leftPanelloadingCharts: {},
	currentPage: 1,
	totalPages: null,
	totalItems: null,
	pageSize: DEFAULT_PAGE_SIZE,
	allChartsLoaded: false,
	isLoadingBatch: false,
	aiSummaries: {},
	loadingAISummaries: {},
	failedAISummaries: {},
};

export const DEFAULT_VIRTUAL_STATE = {
	visibleStartIndex: 0,
	visibleEndIndex: VISIBLE_CHART_COUNT - 1,
	isVirtualEnabled: false,
	totalCharts: 0,
	scrollTop: 0,
	lastScrollDirection: "down",
	isUpdating: false,
};

export const DEFAULT_VIRTUAL_REF = {
	isUpdating: false,
	lastScrollTop: 0,
	scrollDirection: "down",
	updateTimeout: null,
	stableDirection: "down",
	directionCount: 0,
	lastBatchUpdate: 0,
	pendingUpdate: null,
};

export const DEFAULT_REQUEST_QUEUE = {
	pending: new Set(),
	processing: new Set(),
	completed: new Set(),
	failed: new Set(),
	pendingPages: new Set(),
	processingPages: new Set(),
	completedPages: new Set(),
	failedPages: new Set(),
};

export const AXIS_LABELS = {
	Seg: "Segment",
	"A-L1": "Attributes",
	"A-L2": "Attributes",
	RC: "Y-axis",
};

export enum ExportPPTViewType {
	Manage = "export-ppt-manage",
	Charts = "export-ppt-charts",
	Preview = "export-ppt-preview",
}

export interface ISection {
	section_name: string;
	sub_sections: ISubSection[];
}

export interface ISectionsResponse {
	message: string;
	data: { sections: ISection[]; global_filters: IDashboardFilterDropdown[]; active_insights_id: string; filter_restrictions?: IFilterSelectionRestriction };
	error: unknown;
}

export enum PositionType {
	LEFT = "left",
	RIGHT = "right",
}

export interface ILastSelectedEntry {
	sectionKey: string;
	analysisId: string | number;
	slideName?: string;
	chartTitle?: string;
}

export interface IQuantDashboardState {
	sections: ISection[];
	activeSectionKey?: string;
	selectedAnalysisId?: string;
	selectedSlideName?: string;
	globalFilters: IDashboardFilterDropdown[];
	localFilters: IDashboardFilterDropdown[];
	isFocusMode: boolean;
	globalFilterDropdownCatogeries: IGlobalDimensionDropdown[];
	globalFilterDimensions: IGlobalDimension[];
	defaultFilter: IDashboardCacheData;
	overriddenFilter: IDashboardCacheData;
	isDashboardFilterLoading: boolean;
	isDashboardFilterOpen: boolean;
	isMultiTileSlide: boolean;
	quantDashbaordActiveInsightsId: string;
	searchOpen: boolean;
	defaultGlobalFilters: IDashboardFilterDropdown[];
	filterSelectionRestriction: IFilterSelectionRestriction | null;
	chartTitle: string;
	scaffold: IScaffoldMeta;
	/** Persists the last selected section + analysis per study so the dashboard
	 *  can restore the user's position when they revisit a study. Keyed by study_id. */
	lastSelectedMap: Record<string, ILastSelectedEntry>;
	currentAnalysisResetState: ICurrentAnalysisResetStateType;
}

export enum ChartRenderedFromType {
	Dashboard = "dashboard",
	AskKural = "ask-kural",
	AskKuralInline = "ask-kural-inline",
}

export interface IGlobalDimension {
	dimensionName: string;
	dimensionKey: string;
	order: number;
}

export interface IFilterValues {
	id: string;
	value: string;
}
export interface IFilterSelectionRestriction {
	global?: number;
	local?: {
		[key: string]: number;
	};
}

export interface IDashboardFilterDropdown {
	field_name: string;
	field_values: IFilterValues[];
	selected_values: string[];
	is_global: boolean;
	is_custom_segment: boolean;
	is_numerical: boolean;
	is_question: boolean;
	axis_category: string;
	mode: string;
	is_apply_to_all: boolean;
	order?: number;
	field_key?: string;
}

export interface IGlobalDimensionDropdown {
	title: string;
	key: string;
	value: string;
	children?: IGlobalDimensionDropdown[];
}

export interface IResetQuantDashboardAnalysisResponse {
	message: string;
	data: { dropdowns: IDashboardFilterDropdown[]; global_filters: IDashboardFilterDropdown[] };
	error: unknown;
}

export interface IDropdownAllOptionsResponse {
	message: string;
	data: { split_by: IGlobalDimensionDropdown[] };
	error: unknown;
}
export interface IDropdown {
	field_name: string;
}
export interface IAnalysis {
	analysis_id: string;
	slide_name?: string;
	question_text: string;
	chart_title: string;
	dropdowns?: IDashboardFilterDropdown[];
}

export interface ISubSection {
	sub_section_name: string;
	analyses: IAnalysis[];
}

export interface IApiSearchItem {
	section_name: string;
	sub_section_name: string;
	question_text: string;
	chart_title: string;
	analysis_id: string;
	slide_name?: string;
}

export interface ISearchAnalysis {
	sectionName: string;
	subSectionName: string;
	analysis: {
		analysis_id: string;
		slide_name?: string;
		question_text: string;
		chart_title: string;
		dropdowns: any[];
	};
}

export interface IGroupedResult {
	sectionName: string;
	analyses: ISearchAnalysis[];
}

export interface ISearchPopoverProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSelectAnalysis?: (analysis: any, subSection?: any) => void; // <-- add this
}

export interface IDashboardCacheData {
	[key: string]: ICacheDataFormat;
}

export interface ICacheDataFormat {
	[key: string]: {
		dimensionName?: string;
		dimensionKey?: string;
		selectedValues?: string[];
		isGlobal?: boolean;
	};
}

export type ChartRulesType = Record<string, any>;

export interface IHierarchyDef {
	dimension: string;
	order: number;
	orientation: "column" | "row";
	values: string[];
}

export interface IHierarchySplit {
	columnHierarchies: IHierarchyDef[];
	rowHierarchy: IHierarchyDef | null;
}

export interface IChartResponse {
	error: {
		error_message?: string;
		error_type?: string;
		is_error?: boolean;
		is_retry?: boolean;
	};
	from_cache: boolean;
	sections: any;
}

/** Individual pairwise comparison within a CI row */
export interface IStatMarkerItem {
	compared_with: string;
	marker_label: string;
	p_value: number | null;
	direction: "higher" | "lower" | null;
}

/** One CI row on a series item */
export interface IStatMarkerCIRow {
	ci_level: string;
	items: IStatMarkerItem[];
}

/** Lean per-series stat_marker */
export interface IStatMarker {
	enabled: boolean;
	ci_rows: IStatMarkerCIRow[];
}

/** Per-CI config — style + marker mapping unified, no nesting */
export interface IStatTestCIConfig {
	marker_type: string;
	markers: Record<string, string>;
	font_size: number;
	font_color: string;
	font_weight: number;
	background_enabled: boolean;
	background_color: string | null;
	border_color: string | null;
	border_width: number;
}

/** chart_config.stat_test_config — flat, no meta/style wrappers */
export interface IStatTestConfig {
	enabled: boolean;
	application_method: "compare_all" | "reference_based";
	comparison_axis: "x_across_y" | "y_across_x" | "compare" | "gf1" | "gf2";
	reference_item: string | null;
	marker_placement: "on_reference" | "on_non_reference" | null;
	show_p_value: boolean;
	always_icon_mode: boolean;
	confidence: Record<string, IStatTestCIConfig>;
	separate_marker_tooltip?: boolean;
	marker_border_color?: string;
}

/** Stat marker layout config specific to each chart type */
export interface IStatMarkerLayoutConfig {
	/** Extra grid padding when markers are present (px) */
	gridPadding: number;
	/** Minimum segment/bar pixel width to show markers (0 = always show) */
	minSegmentWidth: number;
}

export interface IStatMarkerEntry {
	code: string;
	label: string;
}

export interface IParsedStatMarkers {
	prefix: string;
	markerConfidence: string;
	entries: IStatMarkerEntry[];
}

export interface IChartPayloadItem {
	order?: number;
	[key: string]: any;
}

export interface ISortedChartPayloadItem {
	[key: string]: any;
}

// models/interfaces.ts — ADD these new types

/** Loading status for individual chart slots in scaffold flow */
export enum ChartSlotStatusType {
	IDLE = "idle",
	LOADING = "loading",
	SUCCEEDED = "succeeded",
	FAILED = "failed",
}

/** Stub returned by the scaffold API for each chart position */
export interface IChartStub {
	analysis_id: string;
	chart_title?: string;
	chart_subtitle?: string;
	chart_type?: string;
	analysis_type: string;
	slide_name?: string;
	question_number: string;
	view_position: number;
	view_layout?: string;
	view_chart_count?: number;
	view_chart_count_display?: string;
	split_by?: any[];
}

/** Per-slot error information */
export interface ISlotError {
	message: string;
	httpStatus?: number;
	isRetryable: boolean;
}

/** Per-slot state — one per chart stub from scaffold response */
export interface IChartSlotState {
	stub: IChartStub;
	status: ChartSlotStatusType;
	error: ISlotError | null;
	chartData: any | null;
}

/** Scaffold metadata grouped together for cleaner state shape */
export interface IScaffoldMeta {
	loading: boolean;
	error: string | null;
	slideName: string | null;
	viewLayout: string | null;
	viewChartCount: number;
	/** Per-slot states keyed by view_position (1-based) */
	slots: Record<number, IChartSlotState>;
}

export interface ISegmentVersionInfo {
	segment_id: string;
	segment_version: number;
}

export interface ICheckSegmentUpdatesPayload {
	study_id: string;
	active_insights_id: string;
	segments: ISegmentVersionInfo[];
}

export interface ICheckSegmentUpdatesResponse {
	message: string;
	data: {
		update: boolean;
	};
}

// src/types/customTemplate.ts

// In your interfaces file — replace the template-related types with:

/** Enum representing the current view state of the upload flow */
export enum TemplateModalViewType {
	Upload = "upload",
	Processing = "processing",
	Preview = "preview", // was "Complete" — now shows preview with confirm/re-upload
	Error = "error",
}

/** Enum representing backend processing stages */
export enum TemplateProcessingStageType {
	Uploading = 0,
	Extracting = 1,
	Analyzing = 2,
	Generating = 3,
	Complete = 4,
}

/** Maps backend pipeline_stage strings to the frontend enum */
export const PIPELINE_STAGE_MAP: Record<string, TemplateProcessingStageType> = {
	uploading: TemplateProcessingStageType.Uploading,
	extracting: TemplateProcessingStageType.Extracting,
	ai_analyzing: TemplateProcessingStageType.Analyzing,
	generating_preview: TemplateProcessingStageType.Generating,
	complete: TemplateProcessingStageType.Complete,
};

/** Maps each processing stage to its display message */
export const PROCESSING_STAGE_MESSAGES: Record<Exclude<TemplateProcessingStageType, TemplateProcessingStageType.Complete>, string> = {
	[TemplateProcessingStageType.Uploading]: "Uploading your template...",
	[TemplateProcessingStageType.Extracting]: "Extracting template structure...",
	[TemplateProcessingStageType.Analyzing]: "AI analyzing your template...",
	[TemplateProcessingStageType.Generating]: "Generating preview export...",
};

/** Matches actual GET /quant/templates/status response data shape */
export interface ITemplateStatusData {
	template_id: string;
	name: string;
	pipeline_stage: string;
	pipeline_stage_message: string;
	pipeline_stages_completed: string[];
	status: string;
	status_message: string;
	pipeline_error: string | null;
	mapping_summary?: Record<string, any>;
	slide_dimensions?: { width_inches: number; height_inches: number };
}

/** Wrapper response from status endpoint */
export interface ITemplateStatusResponse {
	message: string;
	data: ITemplateStatusData;
	error: unknown;
}

/** Matches GET /quant/templates/preview response data shape */
export interface ITemplatePreviewData {
	presigned_url: string;
	filename: string;
	expires_in: number;
}

export interface ITemplatePreviewResponse {
	message: string;
	data: ITemplatePreviewData;
	error: unknown;
}

/** Result returned by the useTemplateUpload hook after upload+complete step */
export interface ITemplateUploadResult {
	success: boolean;
	templateId?: string;
	templateName?: string;
	error?: string;
	errorCode?: number;
	isConflict?: boolean;
}

export interface IAutoManageTemplateProps {
	/** Called with the new template ID so parent can select it in dropdown */
	onTemplateUploaded?: (templateId: string, templateName: string) => void;
}

/** Max file size: 25MB — matches ManageTemplate */
export const MAX_TEMPLATE_FILE_SIZE = 25 * 1024 * 1024;

/** Polling interval in milliseconds */
export const POLLING_INTERVAL_MS = 3000;

export interface ICurrentAnalysisResetStateType {
	analysisId: string | number | null;
	slideName: string | null;
	isResetEnabled: boolean | null;
	isResetDisabled: boolean | null;
}